// @ts-nocheck
/**
 * KL HTTP API 基类实现
 *
 * 本文件定义了 HTTP API 的抽象基类，提供：
 * - 统一的 API 接口定义
 * - 请求配置管理
 * - 响应数据处理
 * - 超时控制
 * - 请求头和参数管理
 *
 */

import type { KLHttpResponse, KLHttpMethod } from './types'
import { klHttpClient } from './kl_http_client'
import type { KLHttpRequestOptions } from './types'

/**
 * HTTP API 抽象基类
 *
 * 提供统一的 HTTP API 接口定义和基础功能实现
 * 子类可以继承此类来定义具体的 API 接口
 *
 * @abstract
 * @class KLHttpApi
 * @template TResp 处理后的响应数据类型
 * @template TRespRaw 原始响应数据类型
 * @template TData 请求数据类型
 */
export abstract class KLHttpApi<TResp = any, TRespRaw = any, TData = any> {
	/** 请求超时时间（毫秒），默认 10 秒 */
	public timeout?: number = 10000

	/** 基础 URL */
	protected mBaseURL?: string

	/**
	 * 获取基础 URL
	 *
	 * @returns string | undefined 基础 URL
	 */
	get baseURL(): string | undefined {
		return this.mBaseURL
	}

	/** 请求 URL */
	public url: string

	/** HTTP 请求方法，默认为 GET */
	public method: KLHttpMethod = 'GET'

	/** 请求头存储 */
	protected mHeaders: Record<string, string> = {}

	/**
	 * 获取请求头
	 *
	 * @returns Record<string, any> 请求头对象
	 */
	get headers(): Record<string, any> {
		return this.mHeaders
	}

	/** 查询参数存储 */
	protected mParams: Record<string, any> = {}

	/**
	 * 获取查询参数
	 *
	 * @returns Record<string, any> 查询参数对象
	 */
	get params(): Record<string, any> {
		return this.mParams
	}

	/** 请求体数据 */
	public data?: TData

	/** 是否携带凭证（cookies、authorization headers 等） */
	protected mWithCredentials: boolean = false

	/**
	 * 构造函数
	 *
	 * @param url 请求 URL
	 * @param method HTTP 请求方法，默认为 GET
	 * @protected
	 */
	protected constructor(url: string, method: KLHttpMethod = 'GET') {
		this.url = url
		this.method = method
	}

	/**
	 * 设置是否携带凭证
	 *
	 * 启用后，请求会携带 cookies、authorization headers 等凭证信息
	 * 用于跨域请求时发送/接收 cookies
	 *
	 * @param withCredentials 是否携带凭证
	 * @returns this 用于链式调用
	 * @protected
	 */
	protected setWithCredentials(withCredentials: boolean): this {
		this.mWithCredentials = withCredentials
		return this
	}

	/**
	 * 批量设置查询参数
	 *
	 * 将新的参数与现有参数合并，新参数会覆盖同名的现有参数
	 *
	 * @param params 要设置的查询参数
	 * @protected
	 */
	protected setParams(params: Record<string, unknown>) {
		this.mParams = {
			...this.mParams,
			...params,
		}
	}

	/**
	 * 批量设置请求头
	 *
	 * 将新的请求头与现有请求头合并，新请求头会覆盖同名的现有请求头
	 *
	 * @param headers 要设置的请求头
	 * @protected
	 */
	protected setHeader(headers: Record<string, any>) {
		this.mHeaders = {
			...this.mHeaders,
			...headers,
		}
	}

	/**
	 * 设置请求体中的单个字段
	 *
	 * 用于将参数作为请求体对象的一个字段，而不是整个请求体
	 *
	 * @param key 字段名
	 * @param value 字段值
	 * @protected
	 */
	protected setBodyField(key: string, value: any) {
		if (!this.data || typeof this.data !== 'object') {
			this.data = {} as TData
		}
		;(this.data as any)[key] = value
	}

	/**
	 * 组装请求选项
	 *
	 * 将当前实例的配置组装成完整的请求选项对象
	 *
	 * @param timeout 超时时间，如果提供则覆盖实例的默认超时时间
	 * @returns KLHttpRequestOptions 组装后的请求选项
	 * @protected
	 */
	protected assemblyRequestOptions(timeout?: number): KLHttpRequestOptions {
		return {
			timeout: timeout || this.timeout,
			baseURL: this.baseURL,
			headers: this.headers,
			url: this.url,
			method: this.method,
			params: this.mParams,
			data: this.data,
			withCredentials: this.mWithCredentials,
		}
	}

	/**
	 * 执行 HTTP 请求
	 *
	 * 核心方法，负责发送 HTTP 请求并处理响应
	 *
	 * @param timeout 超时时间，用于精细化控制超时时间
	 * @returns Promise<KLHttpResponse<TResp>> 处理后的响应对象
	 *
	 * @example
	 * ```typescript
	 * class UserApi extends KLHttpApi<UserInfo> {
	 *   constructor() {
	 *     super('/api/user/123', 'GET')
	 *   }
	 * }
	 *
	 * const userApi = new UserApi()
	 * const response = await userApi.exe()
	 * ```
	 */
	exe(timeout?: number): Promise<KLHttpResponse<TResp>> {
		const options = this.assemblyRequestOptions(timeout)
		return klHttpClient.request<TResp, TRespRaw>(options, this.onResponse)
	}

	/**
	 * 响应处理钩子
	 *
	 * 子类可以重写此方法来自定义响应数据的处理逻辑
	 * 默认直接返回原始响应
	 *
	 * @param response 原始响应对象
	 * @returns KLHttpResponse<TResp> | Promise<KLHttpResponse<TResp>> 处理后的响应对象
	 * @protected
	 *
	 * @example
	 * ```typescript
	 * class UserApi extends KLHttpApi<UserInfo, ApiResponse<UserInfo>> {
	 *   protected onResponse(response: KLHttpResponse<ApiResponse<UserInfo>>): KLHttpResponse<UserInfo> {
	 *     // 提取 data 字段作为响应数据
	 *     return {
	 *       ...response,
	 *       data: response.data.data
	 *     }
	 *   }
	 * }
	 * ```
	 */
	protected onResponse(
		response: KLHttpResponse<TRespRaw>,
	): KLHttpResponse<TResp> | Promise<KLHttpResponse<TResp>> {
		return response
	}
}
